import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
  DATA_STUDIO_COLUMN_PAGE_SIZE,
  getDataStudioTableColumns,
} from "../services/dataStudio/dataStudioTablesApi";
import type {
  DataStudioProfileColumn,
  DataStudioProfileColumnPageInfo,
  DataStudioProfileColumnWindow,
  GetDataStudioTableColumnsResponse,
} from "../services/dataStudio/dataStudioTables.types";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { ConnectorAssetIdentity } from "../services/dataStudio/dataStudioAssets.types";

/**
 * Pages evicted from the opposite end once the cache holds this many —
 * mirrors `MAX_CACHED_ROW_PAGES` in `useDataStudioTableProfileRows`, bounding
 * memory for a table a user scrolls through at length in either horizontal
 * direction without limiting how far they can scroll.
 */
const MAX_CACHED_COLUMN_PAGES = 5;

export interface UseDataStudioTableProfileColumnsResult {
  /** Every page received so far, flattened in arrival order. */
  columns: DataStudioProfileColumn[];
  /**
   * Paging state of the currently loaded columns: `hasPrevious`/`previousOffset`
   * come from the first cached page, `hasMore`/`nextOffset` from the last —
   * either end can be the one still connected to the collection's edge once
   * pages start getting evicted from the opposite end.
   */
  columnPageInfo: DataStudioProfileColumnPageInfo | undefined;
  /**
   * The offset of the first cached page and how many columns are currently
   * loaded — `useDataStudioTableProfileRows` mirrors this onto the rows
   * endpoint's own column paging so a row's cells cover the columns actually
   * rendered. `undefined` before the first page has loaded.
   */
  columnWindow: DataStudioProfileColumnWindow | undefined;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  /** True while an earlier column page is in flight. */
  isLoadingOlderColumns: boolean;
  /** True while another earlier page remains to be fetched. */
  hasOlderColumns: boolean;
  /** Backend's own reason when a page request fails, else null. */
  errorMessage: string | null;
  /** Requests the next (rightward) page; ignored while one is already in flight. */
  loadMore: () => void;
  /** Requests the previous (leftward) page; ignored while one is already in flight. */
  loadOlder: () => void;
}

/**
 * Offset-paged columns for the profile grid, the column-dimension mirror of
 * `useDataStudioTableProfileRows`: scrolling right calls `loadMore` and
 * scrolling left calls `loadOlder`, so the grid stops needing every column of
 * a wide asset up front.
 *
 * The columns API pages by `limit`/`offset` — there is no cursor — so the
 * next and previous page params are the offsets the current page's `meta`
 * implies. `null` ends the collection in that direction, which is what stops
 * React Query fetching further. `maxPages` caps the cache so scrolling far in
 * one direction evicts pages from the opposite end instead of growing
 * unboundedly.
 */
export const useDataStudioTableProfileColumns = (
  identity: ConnectorAssetIdentity | null,
): UseDataStudioTableProfileColumnsResult => {
  const connectionId = identity?.connectionId ?? "";
  const assetId = identity?.assetId ?? "";
  const liveSampleSlug = identity?.liveSampleSlug ?? null;
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () =>
      queryKeys.dataStudio.tableProfileColumns(
        connectionId || "none",
        assetId || "none",
      ),
    [assetId, connectionId],
  );

  const query = useInfiniteQuery<GetDataStudioTableColumnsResponse>({
    queryKey,
    queryFn: ({ pageParam }) =>
      getDataStudioTableColumns({
        connectionId,
        assetId,
        liveSampleSlug,
        offset: (pageParam as number) ?? 0,
        limit: DATA_STUDIO_COLUMN_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.columnPageInfo.nextOffset,
    getPreviousPageParam: (firstPage) => firstPage.columnPageInfo.previousOffset,
    maxPages: MAX_CACHED_COLUMN_PAGES,
    // A sheet switch intentionally resets the grid to its first column. Keeping
    // inactive pages would make React Query replay every stale page when the
    // user returns, even though none of that off-screen data can be reused.
    gcTime: 0,
    enabled: Boolean(connectionId && assetId),
  });

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey, exact: true });
    },
    [queryClient, queryKey],
  );

  const columns = useMemo(
    () => query.data?.pages.flatMap((page) => page.columns) ?? [],
    [query.data],
  );

  /**
   * `pageParams[0]` is the offset the first cached page was actually
   * requested with — the source of truth for the window's start, since
   * `columnPageInfo.previousOffset` only reports the offset a *further*
   * backward page would use, not the current one.
   */
  const columnWindow = useMemo<DataStudioProfileColumnWindow | undefined>(() => {
    if (!query.data || columns.length === 0) return undefined;
    const firstPageOffset = (query.data.pageParams[0] as number | undefined) ?? 0;
    return { offset: firstPageOffset, limit: columns.length };
  }, [query.data, columns.length]);

  const firstPage = query.data?.pages[0];
  const lastPage = query.data?.pages[query.data.pages.length - 1];
  const columnPageInfo = useMemo<DataStudioProfileColumnPageInfo | undefined>(
    () => {
      if (!firstPage || !lastPage) return undefined;
      return {
        total: lastPage.columnPageInfo.total,
        nextOffset: lastPage.columnPageInfo.nextOffset,
        hasMore: lastPage.columnPageInfo.hasMore,
        previousOffset: firstPage.columnPageInfo.previousOffset,
        hasPrevious: firstPage.columnPageInfo.hasPrevious,
      };
    },
    [firstPage, lastPage],
  );

  return {
    columns,
    columnPageInfo,
    columnWindow,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoadingOlderColumns: query.isFetchingPreviousPage,
    hasOlderColumns: Boolean(query.hasPreviousPage),
    errorMessage: query.isError
      ? getApiErrorMessage(query.error, "Unable to load more columns.")
      : null,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetching) {
        query.fetchNextPage();
      }
    },
    loadOlder: () => {
      if (query.hasPreviousPage && !query.isFetching) {
        query.fetchPreviousPage();
      }
    },
  };
};
